// session.tsx — глобальная сессия фронта. При монтировании дёргает /api/me;
// 401 (или любая ошибка) → me=null → показываем экран входа. logout чистит
// серверную куку и сбрасывает состояние. Это единственный держатель «кто я».

import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { api, type Capability, type MeResponse } from '../api/client';

interface SessionState {
  me: MeResponse | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  // Есть ли у вошедшего возможность (Ф8 RBAC). Сервер всё равно проверяет — это
  // только для скрытия пунктов/кнопок. Нет сессии → false.
  can: (cap: Capability) => boolean;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      setMe(await api.me());
    } catch {
      // 401 / сеть / истёкшая сессия — все ведут к экрану входа.
      setMe(null);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await api.logout();
    } finally {
      setMe(null);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const can = (cap: Capability): boolean => me?.capabilities.includes(cap) ?? false;

  return (
    <SessionContext.Provider value={{ me, loading, refresh, logout, can }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession must be used within <SessionProvider>');
  }
  return ctx;
}
